import { CalendarOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, DatePicker, Input, InputRef } from "antd";
import locale from "antd/es/date-picker/locale/es_ES";
import { FilterDropdownProps, Key } from "antd/es/table/interface";
import dayjs, { Dayjs } from "dayjs";
import { useRef, useState } from "react";

const { RangePicker } = DatePicker;

type Dates = {
  start: Dayjs | null;
  finish: Dayjs | null;
};

const useColumnSearch = <T extends object>(isLocalSearch?: boolean) => {
  const searchInput = useRef<InputRef | null>(null);
  const [dates, setDates] = useState<Dates>({ start: null, finish: null });

  const handleSearch = (confirm: FilterDropdownProps["confirm"]) => {
    confirm();
  };

  const handleReset = (clearFilters?: FilterDropdownProps["clearFilters"]) => {
    clearFilters?.();
  };

  const getColumnSearchProps = (
    dataIndex: keyof T,
    isExact?: boolean,
    typeInput?: "input" | "rangepicker",
  ) => {
    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        close,
      }: FilterDropdownProps) => (
        <div
          style={{ padding: 8 }}
          className="block max-w-[265px]"
          onKeyDown={(e) => e.stopPropagation()}
        >
          {typeInput === "rangepicker" ? (
            <RangePicker
              inputReadOnly={true}
              format={"DD-MM-YYYY"}
              defaultValue={[dates.start, dates.finish]}
              disabledDate={(current) =>
                current && current > dayjs().endOf("day")
              }
              defaultOpen={true}
              value={[dates.start, dates.finish]}
              locale={locale}
              onChange={(dates) => {
                try {
                  if (dates) {
                    setDates({
                      start: dates[0],
                      finish: dates[1],
                    });
                  }
                } catch (e) {
                  console.log(e);
                  setDates({ start: null, finish: null });
                  handleReset(clearFilters);
                  confirm({ closeDropdown: true });
                }
              }}
              style={{ marginBottom: 8 }}
            />
          ) : (
            <Input
              ref={searchInput}
              placeholder="Buscar"
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => handleSearch(confirm)}
              style={{ marginBottom: 8, display: "block" }}
            />
          )}

          <div>
            <div className="flex gap-2">
              <Button
                type="primary"
                className="flex items-center"
                onClick={() => {
                  if (typeInput === "rangepicker") {
                    setSelectedKeys([
                      dates.start?.format("YYYY-MM-DD") || "",
                      dates.finish?.format("YYYY-MM-DD") || "",
                    ]);
                    handleSearch(confirm);
                    return;
                  }
                  handleSearch(confirm);
                }}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
              >
                Buscar
              </Button>
              <Button
                onClick={() => {
                  handleReset(clearFilters);
                  setDates({ start: null, finish: null });
                  confirm({ closeDropdown: true });
                }}
                size="small"
                style={{ width: 90 }}
              >
                Reiniciar
              </Button>
            </div>
            <div className="mt-2">
              <Button
                type="link"
                size="small"
                onClick={() => {
                  close();
                }}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      ),
      filterIcon: () =>
        typeInput === "rangepicker" ? <CalendarOutlined /> : <SearchOutlined />,
      ...(typeInput !== "rangepicker" &&
        isLocalSearch && {
          onFilter: (value: boolean | Key, record: T) => {
            const recordValue = record[dataIndex] as unknown as string;

            if (isExact) {
              return recordValue
                ? recordValue.toLowerCase() === String(value).toLowerCase()
                : false;
            }

            return recordValue
              ? recordValue.toLowerCase().includes(String(value).toLowerCase())
              : false;
          },
        }),
      filterDropdownProps: {
        onOpenChange(open: boolean) {
          if (open) {
            setTimeout(() => searchInput.current?.select(), 100);
          }
        },
      },
      render: (text: string) => text,
    };
  };

  return { getColumnSearchProps };
};

export default useColumnSearch;
